<template>
  <div class="input-text" :class="[{ 'input-text--error': errors.length }, { 'input-text--tooltip': tooltipText }]">
    <label v-if="label" class="input-text__label" :for="name">
      {{ label }} <span v-if="errorMessage?.trim()">- {{ errorMessage }}</span>
    </label>
    <div class="input-text__wrapper">
      <span v-if="prefix" class="input-text__prefix pointer-events-none">{{ prefix }}</span>
      <UIIcon v-if="leftIcon" class="input-text__left-icon" :name="leftIcon" filled />
      <UITooltip
        v-if="tooltipText"
        placement="bottom-start"
        :fallback-placements="['bottom-start', 'top-start']"
        class="input-text__tooltip"
      >
        <UIIcon name="info-circle-filled" />
        <template #content>
          <div class="input-text__tooltip-content">
            {{ tooltipText }}
          </div>
        </template>
      </UITooltip>
      <input
        :id="`${name}-input`"
        ref="input"
        :value="shallowValue"
        :name
        :autocomplete="autocomplete"
        :disabled="disabled"
        :inputmode="inputmode"
        :readonly="readOnly"
        class="input-text__input py-2"
        :class="[
          { 'input-text__input--error': errors.length },
          prefix || leftIcon ? 'px-4 pl-[44px]' : 'px-4',
          suffix || rightIcon ? 'px-4 pr-[44px]' : 'px-4',
          compact ? 'min-h-10' : 'min-h-12',
          inputClass,
        ]"
        :type="getInputType()"
        :placeholder="placeholder"
        :maxlength="maxLength"
        :step="type === 'number' ? 'any' : undefined"
        :enter-key-hint="enterkeyhint"
        @focus="handleFocus"
        @input="handleInput"
        @blur="handleBlurEvent"
      />
      <span v-if="suffix" class="input-text__suffix">{{ suffix }}</span>
      <UIIcon v-if="rightIcon" class="input-text__right-icon pointer-events-none" :name="rightIcon" filled />
      <UIBaseTransition>
        <UIIcon
          v-if="clearable && inputValue"
          class="input-text__clear-icon"
          name="close"
          filled
          @click="handleClear"
        />
      </UIBaseTransition>
      <span v-if="suffix" class="input-text__suffix">{{ suffix }}</span>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
import { useField } from 'vee-validate'
import { MaskInput } from 'maska'
import type { InputTextProps } from '@/types/ui'

const props = withDefaults(defineProps<InputTextProps<T>>(), {
  autocomplete: undefined,
  placeholder: 'Placeholder',
  modelValue: undefined,
  value: undefined,
  id: undefined,
  label: '',
  maxLength: undefined,
  disabled: false,
  leftIcon: '',
  rightIcon: '',
  prefix: '',
  suffix: '',
  type: 'text',
  clearable: false,
  syncVModel: false,
  description: '',
  controlled: true,
  dataMaska: undefined,
  dataMaskaOptions: undefined,
  dataMaskaTokens: undefined,
  enterkeyhint: undefined,
  inputClass: undefined,
})

const emit = defineEmits<{
  (e: 'blur', event: FocusEvent): void
  (e: 'focus', event: FocusEvent): void
  (e: 'update:modelValue', event: T): void
  (e: 'clear', value: ''): void
}>()

const nameRef = toRef(props, 'name')

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  resetField,
  errors,
} = useField<T>(nameRef, undefined, {
  initialValue: props.modelValue || props.value,
  controlled: props.controlled,
  syncVModel: props.syncVModel,
})

const handleClear = () => {
  resetField({
    value: '' as T,
  })

  shallowValue.value = ''

  emit('clear', '')
}

const isFocused = ref(false)

const handleBlurEvent = (event: FocusEvent) => {
  handleBlur(event)
  emit('blur', event)

  isFocused.value = false
}

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true
  emit('focus', event)
}

const input = ref<HTMLInputElement | null>(null)

const maskInput = ref<MaskInput | null>(null)

onMounted(() => {
  if (input.value && (props.dataMaskaTokens || props.dataMaska || props.dataMaskaOptions) && props.type !== 'number') {
    maskInput.value = new MaskInput(
      input.value,
      props.dataMaskaOptions || {
        tokens: props.dataMaskaTokens,
        mask: props.dataMaska,
      },
    )
  }
})

const isCurrencyType = computed(() => {
  return props.type === 'currency'
})

const isPercentageType = computed(() => {
  return props.type === 'percentage'
})

const shallowValue = ref<string | number | undefined>(inputValue.value || '')

const parseToCurrencyFormat = <T extends string | number>(value: T) => {
  const parts = value.toString().split('.')

  const v = parts[0].replace(/\D/g, '')

  const dec = parts[1]

  const n = new Intl.NumberFormat('en-EN').format(v as any)

  return dec !== undefined ? n + '.' + dec : n
}

// no matter the input, format to percentage with max 2 decimal places, min 0 max 100
const parseToPercentageFormat = <T extends string | number>(value: T) => {
  const parts = value.toString().split('.')
  const v = parts[0].replace(/\D/g, '')
  const dec = parts[1]
  const n = new Intl.NumberFormat('en-EN').format(v as any)

  let valueFormatted = dec !== undefined ? n + '.' + dec : n

  valueFormatted = valueFormatted.replace(/[^0-9.]/g, '')

  if (parseFloat(valueFormatted) > 100) {
    valueFormatted = '100'
  }

  if (parseFloat(valueFormatted) < 0) {
    valueFormatted = '0'
  }

  // ensure no more than 2 decimal places
  if (valueFormatted.includes('.')) {
    const parts = valueFormatted.split('.')

    if (parts[1].length > 2) {
      valueFormatted = parts[0] + '.' + parts[1].substring(0, 2)
    }
  }

  return valueFormatted
}

watch(
  () => props.modelValue,
  value => {
    if (value && isCurrencyType.value) {
      shallowValue.value = parseToCurrencyFormat(value)
    } else if (value && isPercentageType.value) {
      shallowValue.value = parseToPercentageFormat(value)
    } else {
      shallowValue.value = value
    }
  },
)

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement

  if (isCurrencyType.value) {
    shallowValue.value = parseToCurrencyFormat(target.value)
    handleChange(parseFloat(shallowValue.value?.replace(/,/g, '')))
  } else if (isPercentageType.value) {
    shallowValue.value = parseToPercentageFormat(target.value)
    handleChange(parseFloat(shallowValue.value?.replace(/,/g, '')))
  } else {
    shallowValue.value = target.value
    handleChange(target.value)
    emit('update:modelValue', target.value as T)
  }
}

const getInputType = () => {
  return isCurrencyType.value || isPercentageType.value ? 'text' : props.type
}

defineExpose({
  input,
  handleClear,
})
</script>

<style scoped lang="scss">
@import '@/styles/mixins.scss';

$basePadding: 16px;
$iconSize: 24px;
$baseTooltipMargin: 12px;
$baseLabelMargin: 16px;

.input-text {
  color: $black-80;
  font-size: 14px;
  line-height: 140%;

  &__tooltip {
    color: $black-60;

    &-content {
      @include body3();
      max-width: 200px;
    }
  }

  &--error {
    .input-text__label {
      color: $error-100;
    }
  }

  &--tooltip {
    .input-text__label {
      margin-left: calc(#{$baseLabelMargin} + #{$baseTooltipMargin} + #{$iconSize});
    }
  }

  &__label {
    display: block;
    margin-bottom: 2px;
    margin-left: 16px;
    font-weight: 500;
    transition: $base-transition color;
  }

  &__wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;

    .input-text {
      flex-grow: 1;
    }
  }

  &__left-icon,
  &__right-icon,
  &__clear-icon {
    width: $iconSize;
    height: $iconSize;
    color: $black-60;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    :deep() {
      svg {
        width: $iconSize;
        height: $iconSize;
      }
    }
  }

  &__left-icon,
  &__prefix {
    position: absolute;
    left: $basePadding;
    top: 50%;
    transform: translateY(-50%);
  }

  &__right-icon,
  &__suffix,
  &__clear-icon {
    position: absolute;
    right: $basePadding;
    top: 50%;
    transform: translateY(-50%);
  }

  &__prefix,
  &__suffix {
    color: $black-60;
    @include body3();
  }

  &__clear-icon {
    cursor: pointer;
  }

  &__input {
    box-shadow: none;
    border-radius: $base-border-radius;
    font-size: 14px;
    color: $black-100;
    transition:
      $base-transition background,
      $base-transition color,
      $base-transition border;
    width: 100%;
    border: $base-border-width solid $black-20;
    line-height: 1;
    appearance: textfield;
    outline: none;
    background: white;

    @media screen and (max-width: 1023px) {
      font-size: 16px;
    }

    @media screen and (max-width: 1023px) {
      font-size: 16px;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &--compact {
      min-height: 40px;
      padding: 8px 16px;
    }

    &--error {
      border: $base-border-width solid $error-100;

      @media screen and (max-width: 1023px) {
        &::placeholder-shown {
          color: $error-100;
        }
      }

      &::placeholder {
        color: $error-100 !important;
      }
    }

    // TODO: Refactor this part, consider to remove position absolute
    &--with-left-icon,
    &--with-prefix {
      padding-left: calc($iconSize + $basePadding + 4px) !important;
    }

    &--with-right-icon,
    &--with-suffix {
      padding-right: calc($iconSize + $basePadding + 4px) !important;
    }

    &:disabled {
      background: $black-05;
      border: $base-border-width solid $black-20 !important;
      color: $black-60;
    }

    &:focus {
      box-shadow: none;
      border: $base-border-width solid $black-100 !important;
    }

    &:hover {
      border: $base-border-width solid $black-60;
    }

    &::placeholder {
      color: $black-40;
    }
  }

  &__description {
    color: $black-60;
    margin-top: 2px;
    display: block;
    margin-left: 16px;
    font-size: 12px;
  }
}
</style>
